// "Did this content actually change?", shared by the admin editor's autosave gate and
// undo-snapshot commit (via `contentEquality`) and the server's publish-diff badge
// (`resources.byId`). Strips the transient/derived state that renders identically but
// compares unequal, then deep-compares. Handles both content shapes: a serialised React
// Flow graph (conversations) and a raw Tiptap doc (unit guides).

import isEqual from 'lodash-es/isEqual';

// Structural so this package takes no reactflow dependency.
type FlowNode = { data?: Record<string, unknown> } & Record<string, unknown>;
type FlowEdge = { type?: unknown } & Record<string, unknown>;
type FlowLike = {
  nodes: FlowNode[];
  edges: FlowEdge[];
  viewport?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFlowLike(content: unknown): content is FlowLike {
  return isRecord(content) && Array.isArray(content['nodes']) && Array.isArray(content['edges']);
}

// The admin editor rewrites every edge's `type` to this on load (older flows persisted
// the built-in 'smoothstep'), so a flow last published before that default reads unequal
// to its own re-saved draft unless the type is normalised away first.
const NORMALISED_EDGE_TYPE = 'deletable';

// `ignorePosition` keeps position for the autosave/undo compare (a node move is an edit)
// but drops it for the publish badge, where layout drift (auto-layout, tidy-up) isn't a
// change worth re-publishing.
function normaliseNode(node: FlowNode, ignorePosition: boolean): unknown {
  const { selected, dragging, width, height, positionAbsolute, data, position, ...rest } = node;
  const base = { ...rest, data: normaliseData(data) };
  return ignorePosition ? base : { ...base, position };
}

function normaliseEdge(edge: FlowEdge): unknown {
  const { selected, animated, type, ...rest } = edge;
  return { ...rest, type: NORMALISED_EDGE_TYPE };
}

function normaliseData(data: FlowNode['data']): unknown {
  // Drop the derived `outgoers` (recomputed from edges on load). Pull `tiptap` out so an
  // explicit `tiptap: undefined` (which a drag-create adds) doesn't read as different from
  // its absence: lodash isEqual distinguishes the two.
  const { outgoers, tiptap, ...dataRest } = data ?? {};
  return tiptap === undefined ? dataRest : { ...dataRest, tiptap: canonicaliseTiptap(tiptap) };
}

// ProseMirror fills schema-default attrs as `null` the moment the editor touches a loaded
// node, so drop null attrs (else the first edit reads as a change) and default a missing
// doc `content` to an empty array.
export function canonicaliseTiptap(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicaliseTiptap);
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  const result: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (key === 'attrs' && child !== null && typeof child === 'object') {
      const attrs: Record<string, unknown> = {};
      for (const [attrKey, attrValue] of Object.entries(child)) {
        if (attrValue !== null) {
          attrs[attrKey] = attrValue;
        }
      }
      result[key] = attrs;
    } else {
      result[key] = canonicaliseTiptap(child);
    }
  }
  if (result['type'] === 'doc' && !Array.isArray(result['content'])) {
    result['content'] = [];
  }
  return result;
}

export function normaliseFlowForCompare(
  flow: FlowLike,
  ignorePosition = false,
): { nodes: unknown[]; edges: unknown[] } {
  return {
    nodes: flow.nodes.map((node) => normaliseNode(node, ignorePosition)),
    edges: flow.edges.map(normaliseEdge),
  };
}

// Position is significant here: the autosave gate and undo snapshots treat a node move as
// an edit.
export function isFlowContentEqual(a: FlowLike, b: FlowLike): boolean {
  return isEqual(normaliseFlowForCompare(a), normaliseFlowForCompare(b));
}

export function isNodeDataContentEqual(a: FlowNode['data'], b: FlowNode['data']): boolean {
  return isEqual(normaliseData(a), normaliseData(b));
}

function normaliseContent(content: unknown): unknown {
  return isFlowLike(content)
    ? normaliseFlowForCompare(content, /* ignorePosition */ true)
    : canonicaliseTiptap(content);
}

/**
 * Whether a draft has diverged from its last-published content across the whole payload
 * (block order, images, links, quiz config, text), for the "unpublished changes" badge.
 * Ignores node layout, so a just-published draft reads as unchanged.
 */
export function contentDiverged(draftContent: unknown, publishedContent: unknown): boolean {
  return !isEqual(normaliseContent(draftContent), normaliseContent(publishedContent));
}
